import React from 'react';
import { Avatar, Box, Paper, Typography } from '@mui/material';
import { ChatBubbleOutline as ChatIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import type { ZpravaDto } from '../../api/JsdhNotifikaceService/service/ChatService';

interface SeznamZpravProps {
    zpravy: ZpravaDto[];
    /** Id prihlaseneho uzivatele - urcuje zarovnani vlastnich bublin. */
    aktualniUzivatelId?: number | null;
    /** Muze uzivatel psat? Meni jen DOPLNKOVY text prazdneho stavu. */
    muzeZapisovat?: boolean;
}

const initialy = (jmeno: string): string =>
    jmeno
        .split(' ')
        .filter(Boolean)
        .map((cast) => cast[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

/**
 * Popisek oddelovace dne. Dnesek a vcerejsek slovem, zbytek datem - cislo dne
 * uprostred zasahu nikomu nic nerekne, kdezto "Dnes" ano.
 */
const popisekDne = (datum: dayjs.Dayjs): string => {
    const dnes = dayjs().startOf('day');
    const den = datum.startOf('day');
    if (den.isSame(dnes)) return 'Dnes';
    if (den.isSame(dnes.subtract(1, 'day'))) return 'Včera';
    return den.year() === dnes.year() ? den.format('D. MMMM') : den.format('D. M. YYYY');
};

/**
 * HO-147 Task 4: seznam zprav kanalu jednotky. `data-testid="seznam-zprav"`
 * je kontrakt s testy - oznacuje misto, kam server skutecne posila data
 * (ne text z okolniho UI, viz past popsana v zadani tasku).
 *
 * POZOR: zadny `border-left` pruh na bublinach - vlastnika produktu to
 * opakovane vraci jako AI-vzhled, vlastni/cizi zprava se rozlisuje jen
 * zarovnanim a vyplni pozadi.
 *
 * <p>Vzhled se ridi nasim navrhem chatu (docs/superpowers/specs/mockups/
 * 2026-08-30-chat-vzhled-nas-design.html): oddelovace dnu, avatar JEN u cizi
 * zpravy (u vlastni je zbytecny - poznam se podle zarovnani) a prazdny stav,
 * ktery rika, co se stane, ne co chybi.</p>
 */
const SeznamZprav: React.FC<SeznamZpravProps> = ({ zpravy, aktualniUzivatelId, muzeZapisovat = false }) => {
    // BE historie chodi od nejnovejsich (viz HistorieStranka) - pro cteni
    // odshora dolu ji otacime az tady, ne na serveru.
    const serazene = [...zpravy].reverse();

    if (serazene.length === 0) {
        return (
            <Box
                data-testid="seznam-zprav"
                sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 1, height: '100%', p: 4, textAlign: 'center',
                }}
            >
                <ChatIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="body1" color="text.secondary">
                    Zatím žádné zprávy
                </Typography>
                {/* Doplnkovy text rika, CO SE STANE - ne co chybi (navrh, sekce 5). */}
                <Typography variant="body2" color="text.secondary">
                    {muzeZapisovat
                        ? 'Napište první — přečtou ji všichni členové jednotky.'
                        : 'Jakmile někdo napíše, zpráva se objeví tady.'}
                </Typography>
            </Box>
        );
    }

    let predchoziDen: string | null = null;

    return (
        <Box data-testid="seznam-zprav" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
            {serazene.map((zprava) => {
                const vlastni = aktualniUzivatelId != null && zprava.autorId === aktualniUzivatelId;
                const cas = dayjs(zprava.createdAt);
                const den = cas.format('YYYY-MM-DD');
                const novyDen = den !== predchoziDen;
                predchoziDen = den;

                const obsah = zprava.smazano
                    ? 'Zpráva byla smazána.'
                    : (zprava.text || (zprava.maPrilohu ? 'Příloha' : ''));

                return (
                    <React.Fragment key={zprava.id}>
                        {novyDen && (
                            // Bez oddelovace je tydenni historie nerozlisitelna stena -
                            // u zpravy je jen cas, takze "14:36" muze byt dnesek i minuly tyden.
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ alignSelf: 'center', px: 1.5, py: 0.25 }}
                            >
                                {popisekDne(cas)}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: vlastni ? 'row-reverse' : 'row',
                                alignItems: 'flex-start',
                                gap: 1,
                            }}
                        >
                            {/* Avatar JEN u cizi zpravy - u vlastni nic nerika, poznam se
                                podle zarovnani a vyplne (shodne s mobilem). */}
                            {!vlastni && (
                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                                    {initialy(zprava.autorJmeno || '?')}
                                </Avatar>
                            )}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.25,
                                    maxWidth: '72%',
                                    bgcolor: vlastni ? 'primary.main' : 'background.paper',
                                    color: vlastni ? 'primary.contrastText' : 'text.primary',
                                }}
                            >
                                {!vlastni && (
                                    <Typography variant="caption" fontWeight={700} display="block">
                                        {zprava.autorJmeno}
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        // Smazana zprava se nesmi cist jako bezny obsah.
                                        fontStyle: zprava.smazano ? 'italic' : 'normal',
                                        opacity: zprava.smazano ? 0.7 : 1,
                                    }}
                                >
                                    {obsah}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', textAlign: 'right', mt: 0.5 }}>
                                    {cas.format('HH:mm')}
                                    {zprava.upravenoAt ? ' (upraveno)' : ''}
                                </Typography>
                            </Paper>
                        </Box>
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default SeznamZprav;
